/**
 * Plan advisor — turns four plain-language answers into a plan recommendation.
 *
 * Kept separate from the wizard UI so the reasoning is readable on its own and
 * can be checked against the plan definitions in packages/types/src/plans.ts:
 *
 *   Free       1 GB/day    7 days    2 members    security, service health, 5 alerts
 *   Pro   €59  10 GB/day  60 days   10 members    + higher limits
 *   Business  €199  50 GB/day  90 days  unlimited  + PII redaction, priority support
 *   Enterprise  custom, dedicated stack, retention beyond 90 days
 *
 * Every answer maps to the *lowest* plan that can carry it; the advice is the
 * highest of those. That way a single hard requirement (say, security
 * monitoring) lifts the recommendation without any answer silently lowering it.
 */

export type PlanKey = 'free' | 'pro' | 'business' | 'enterprise';

export type SystemsAnswer = 'upTo5' | 'upTo25' | 'upTo100' | 'over100';
export type TeamAnswer = 'upTo2' | 'upTo10' | 'over10';
export type NeedKey = 'alerts' | 'security' | 'serviceHealth';
export type ComplianceAnswer = 'yes' | 'no' | 'unsure';
export type RetentionAnswer = 'week' | 'month' | 'twoMonths' | 'quarter' | 'year';

export interface WizardAnswers {
  systems: SystemsAnswer;
  team: TeamAnswer;
  needs: NeedKey[];
  compliance: ComplianceAnswer;
  retention: RetentionAnswer;
}

export interface PlanAdvice {
  plan: PlanKey;
  /** i18n keys naming the answers that drove this outcome. */
  reasons: string[];
  /** Rounded GB/day estimate, always presented as an estimate. */
  estimatedGbPerDay: number;
}

/** Plan hierarchy; index doubles as the comparison rank. */
const PLAN_ORDER: PlanKey[] = ['free', 'pro', 'business', 'enterprise'];

function rank(plan: PlanKey): number {
  return PLAN_ORDER.indexOf(plan);
}

/**
 * Volume estimate. A quiet server or app produces roughly 200 MB of logs a
 * day; busy web servers and firewalls produce multiples of that, which is why
 * the result is labelled an estimate and the buckets round upward.
 */
const GB_PER_SYSTEM_PER_DAY = 0.2;

/** Upper bound of each bucket — "up to 25 systems" is costed as 25. */
const SYSTEMS_IN_BUCKET: Record<SystemsAnswer, number> = {
  upTo5: 5,
  upTo25: 25,
  upTo100: 100,
  over100: 250,
};

const SYSTEMS_PLAN: Record<SystemsAnswer, PlanKey> = {
  upTo5: 'free',
  upTo25: 'pro',
  upTo100: 'business',
  over100: 'enterprise',
};

const TEAM_PLAN: Record<TeamAnswer, PlanKey> = {
  upTo2: 'free',
  upTo10: 'pro',
  over10: 'business',
};

/** Every plan has alerts, security monitoring and service health (ADR-0105); Free with small limits. */
const NEED_PLAN: Record<NeedKey, PlanKey> = {
  alerts: 'free',
  security: 'free',
  serviceHealth: 'free',
};

/**
 * The cybersecurity law (NIS2, implemented in the Netherlands as the
 * Cyberbeveiligingswet) asks for detection of incidents, log retention and
 * reporting inside 24 hours. Security monitoring is on every plan, but keeping
 * logs long enough to evidence that starts at Business (90 days). "Unsure" deliberately does not push the
 * plan up: guessing on someone's behalf would sell them a tier they may not need.
 */
const COMPLIANCE_PLAN: Record<ComplianceAnswer, PlanKey> = {
  yes: 'business',
  no: 'free',
  unsure: 'free',
};

const RETENTION_PLAN: Record<RetentionAnswer, PlanKey> = {
  week: 'free',
  month: 'pro',
  twoMonths: 'pro',
  quarter: 'business',
  year: 'enterprise',
};

export function recommendPlan(answers: WizardAnswers): PlanAdvice {
  const sizing: Array<{ plan: PlanKey; reason: string }> = [
    { plan: SYSTEMS_PLAN[answers.systems], reason: `planWizard.reason.systems.${answers.systems}` },
    { plan: TEAM_PLAN[answers.team], reason: `planWizard.reason.team.${answers.team}` },
    { plan: RETENTION_PLAN[answers.retention], reason: `planWizard.reason.retention.${answers.retention}` },
  ];
  const needs = answers.needs.map((need) => ({
    plan: NEED_PLAN[need],
    reason: `planWizard.reason.need.${need}`,
  }));
  // Wanting to be compliant is a requirement the customer stated, so it is
  // confirmed in the result exactly like the other requested capabilities.
  if (answers.compliance === 'yes') {
    needs.push({ plan: COMPLIANCE_PLAN.yes, reason: 'planWizard.reason.compliance' });
  }

  const plan = [...sizing, ...needs].reduce<PlanKey>(
    (highest, driver) => (rank(driver.plan) > rank(highest) ? driver.plan : highest),
    'free',
  );

  const reasons = [
    // Sizing answers only earn a line when they actually required this plan;
    // otherwise they read as reasons while a cheaper plan carried them too.
    ...sizing.filter((driver) => driver.plan === plan).map((driver) => driver.reason),
    // Requested capabilities are always confirmed. The customer asked for them
    // and every plan above the one that introduces them includes them, so
    // filtering by the winning plan would silently drop the answer.
    ...needs.map((driver) => driver.reason),
  ];

  return {
    plan,
    reasons: reasons.length > 0 ? reasons : ['planWizard.reason.fits'],
    estimatedGbPerDay: Math.round(SYSTEMS_IN_BUCKET[answers.systems] * GB_PER_SYSTEM_PER_DAY),
  };
}

/** Where the result CTA points per plan. */
export function planCtaHref(plan: PlanKey): string {
  return plan === 'enterprise'
    ? '/contact'
    : `https://app.logpulse.io/sign-up${plan === 'free' ? '' : `?tier=${plan}`}`;
}
